import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../config';
import { fetchEvohomeDevices, EvohomeDevice } from './evohomeApi';
import { eventBus } from './EventBus';
import { patchRoomCardLive } from '../utils/roomCardIdentity';

export const mapaEvohome: Record<number, EvohomeDevice> = {};
let loaded = false;
let socket: Socket | null = null;
/** Un solo GET a la vez: un refresco vacío o atrasado no pisa al que ya mostró las tarjetas. */
let refreshInFlight: Promise<EvohomeDevice[]> | null = null;

export function isMapaEvohomeCargado(): boolean {
  return loaded;
}

export function getEvohomeByHabitacion(habitacionId: number): EvohomeDevice[] {
  const roomId = Number(habitacionId);
  return Object.values(mapaEvohome).filter(
    (d) => Number(d.habitacion_id) === roomId && d.enabled !== false,
  );
}

/** true si alguna zona tiene este Shelly como caldera y el ciclo de frío lo está sosteniendo. */
export function isHeatSupplyFrostHolding(deviceId: number): boolean {
  const id = Number(deviceId);
  if (!Number.isFinite(id)) return false;
  return Object.values(mapaEvohome).some(
    (z) => Number(z.heat_supply_device_id) === id && Boolean(z.heat_supply_frost_holding),
  );
}

function mergeList(devices: EvohomeDevice[]) {
  let changed = false;
  devices.forEach((d) => {
    if (!d?.id) return;
    const id = Number(d.id);
    mapaEvohome[id] = { ...(mapaEvohome[id] || {}), ...d, id };
    changed = true;
  });
  if (changed) eventBus.emit('evohome:updated');
}

function mergeLive(devices: Array<Partial<EvohomeDevice> & { id?: number }>) {
  let changed = false;
  devices.forEach((d) => {
    const id = Number(d?.id);
    if (!Number.isFinite(id)) return;
    const next = patchRoomCardLive(mapaEvohome[id], { ...d, id });
    if (!next) return;
    mapaEvohome[id] = next;
    changed = true;
  });
  if (changed) eventBus.emit('evohome:updated');
}

export function upsertEvohomeDevice(device: EvohomeDevice): void {
  if (!device?.id) return;
  mergeLive([device]);
}

/** Actualiza el mapa local (respuesta de control / refresh / PATCH). */
export function patchEvohomeDevice(device: EvohomeDevice): void {
  upsertEvohomeDevice(device);
}

async function refrescarEvohomeNow(): Promise<EvohomeDevice[]> {
  try {
    const list = await fetchEvohomeDevices();
    if (!Array.isArray(list)) return Object.values(mapaEvohome);
    if (list.length === 0 && Object.keys(mapaEvohome).length > 0) {
      loaded = true;
      return Object.values(mapaEvohome);
    }
    mergeList(list);
    loaded = true;
    return Object.values(mapaEvohome);
  } catch {
    loaded = true;
    return Object.values(mapaEvohome);
  }
}

export function refrescarEvohome(): Promise<EvohomeDevice[]> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refrescarEvohomeNow().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function cargarEvohome(): Promise<EvohomeDevice[]> {
  ensureSocket();
  return refrescarEvohome();
}

function ensureSocket() {
  if (socket) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }
  socket = io(serverUrl, {
    path: '/socket.io-evohome',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
  });
  socket.on('evohome_status', (data: Partial<EvohomeDevice> & { id?: number }) => {
    if (!data?.id) return;
    const id = Number(data.id);
    const existing = mapaEvohome[id];
    if (!existing) {
      void refrescarEvohome();
      return;
    }
    mergeLive([{ ...data, id }]);
  });
}
